import {useCallback, useMemo} from 'react';
import {useFormikContext} from 'formik';
import {useProductContext} from 'contexts/ProductContext';
import {ProductContextType} from 'components/customer/Product/Specs';
import {CabinetDrawerType} from 'components/customer/Product/entity/Cabinet';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {toNumber} from 'components/customer/Product/Drawer/helpers';

// As the per drawer rows did it: only a custom inner drawer product lets a
// drawer go, never its last one, and the count follows.
export const useDeleteInnerDrawer = () => {
    const {productDataStore} = useProductContext<ProductContextType>();
    const {setFieldValue, values} = useFormikContext<DrawerFields>();

    const isCustomInnerDrawer = useMemo(() => {
        const drawerType =
            productDataStore?.current?.cabinet?.attributes?.drawer_type;

        return (
            CabinetDrawerType[drawerType as keyof typeof CabinetDrawerType] ===
            CabinetDrawerType.inner_custom
        );
    }, []);

    // Its space goes to the drawer below, whose slot then reaches up to the
    // one above, the reverse of placing a drawer; lost, it would leave drawers
    // set by hand with no room to move. The bottom drawer's goes to no one, as
    // when it is moved up. Those left count as set by hand, as after placing
    // one, so the count moves none of them; Back to auto re-spaces them.
    const deleteDrawer = useCallback(
        (index: number) => {
            const drawers = values.drawers ?? [];
            const removed = drawers[Number(index)];
            const next = drawers
                .filter((_, i) => i !== index)
                .map((drawer) => ({...drawer, touched: true}));
            const below = next[Number(index)];
            const isBottom = index >= Number(values.drawer_amount) - 1;

            if (removed && below && !isBottom) {
                next[Number(index)] = {
                    ...below,
                    drawer_face_height:
                        toNumber(below.drawer_face_height) +
                        toNumber(removed.drawer_face_height),
                };
            }

            void setFieldValue(
                'drawer_amount',
                Number(values.drawer_amount) - 1
            );
            void setFieldValue('drawers', next);
        },
        [values, setFieldValue]
    );

    return {
        canDelete: isCustomInnerDrawer && Number(values.drawer_amount) > 1,
        deleteDrawer,
    };
};
